import InsightItem from 'components/InsightItem';
import SegmentedBar from 'components/SegmentedBar';
import { useAtomValue } from 'jotai';
import React from 'react';
import { Card } from 'reactstrap';

import { waListingDataAtom } from './state';
import { WAFilter } from '../config';
import { useWAListFilterValue } from '../hooks';

const WAInsights: React.FC = () => {
  const listingData = useAtomValue(waListingDataAtom);
  const selectedWebAppsFilter = useWAListFilterValue<
    'active' | 'inactive' | 'deleted'
  >('filter');
  const insights = listingData?.insights;

  if (!listingData || !insights || Object.keys(insights).length === 0) {
    return <div>Loading...</div>;
  }

  // Extract stats from insights
  const stats = {
    active: insights.status_breakdown.active,
    inactive: insights.status_breakdown.inactive,
    deleted: insights.status_breakdown.deleted,
    total_vulnerabilities: insights.total_vulnerabilities,
    urgent: insights.severity_breakdown.urgent,
    critical: insights.severity_breakdown.critical,
    serious: insights.severity_breakdown.serious,
    medium: insights.severity_breakdown.medium,
    minimal: insights.severity_breakdown.minimal,
    no_web_service: insights.scan_status_breakdown.no_web_service,
    running: insights.scan_status_breakdown.running,
    error: insights.scan_status_breakdown.error,
    auth_failed: insights.scan_status_breakdown.auth_failed,
  };

  return (
    <div className="container-fluid border border-light rounded p-0 mb-1">
      <Card
        className="mb-0 px-3 py-1"
        style={{ backgroundColor: 'rgba(34, 42, 66, 0.02)' }}
      >
        <div className="d-flex flex-wrap align-items-center">
          {/* Severity */}
          {selectedWebAppsFilter !== WAFilter.DELETED && (
            <div
              className="mr-2 pr-2 flex-grow-1 border-right"
              style={{ borderColor: '#dee2e6' }}
            >
              <h6 className="mb-0">Severity:</h6>
              <SegmentedBar
                height="5px"
                width="100%"
                segments={[
                  {
                    color: 'urgent',
                    value: stats.urgent,
                    label: 'Urgent',
                  },
                  {
                    color: 'critical',
                    value: stats.critical,
                    label: 'Critical',
                  },
                  {
                    color: 'serious',
                    value: stats.serious,
                    label: 'Serious',
                  },
                  {
                    color: 'medium',
                    value: stats.medium,
                    label: 'Medium',
                  },
                  {
                    color: 'minimal',
                    value: stats.minimal,
                    label: 'Minimal',
                  },
                ]}
                labelDisplay="value"
                tooltip={`Urgent: ${stats.urgent}, Critical: ${stats.critical}, Serious: ${stats.serious}, Medium: ${stats.medium}, Minimal: ${stats.minimal}`}
              />
            </div>
          )}

          {/* Count Summaries */}
          {selectedWebAppsFilter === WAFilter.ACTIVE && (
            <InsightItem
              label="Active"
              value={stats.active}
              tooltip="Total number of active webapps"
            />
          )}
          {selectedWebAppsFilter === WAFilter.INACTIVE && (
            <InsightItem
              label="Inactive"
              value={stats.inactive}
              tooltip="Total number of inactive webapps"
            />
          )}
          {selectedWebAppsFilter === WAFilter.DELETED && (
            <InsightItem
              label="Deleted"
              value={stats.deleted}
              tooltip="Total number of deleted webapps"
            />
          )}

          {stats.no_web_service > 0 && (
            <InsightItem
              label="No Web Service"
              value={stats.no_web_service}
              tooltip="Total number of webapps with scan result 'No Web Service'"
            />
          )}
          {stats.running > 0 && (
            <InsightItem
              label="Running"
              value={stats.running}
              tooltip="Total number of webapps with scan result 'Running'"
            />
          )}
          {stats.error > 0 && (
            <InsightItem
              label="Error"
              value={stats.error}
              tooltip="Total number of webapps with scan result 'Error'"
            />
          )}
          {stats.auth_failed > 0 && (
            <InsightItem
              label="Auth Failed"
              value={stats.auth_failed}
              tooltip="Total number of webapps with auth status 'Auth Failed'"
            />
          )}
          {selectedWebAppsFilter !== WAFilter.DELETED && (
            <InsightItem
              label="Vulnerabilities"
              value={stats.total_vulnerabilities}
              tooltip="Total number of vulnerabilities"
              isLast
            />
          )}
        </div>
      </Card>
    </div>
  );
};

export default WAInsights;
